import React, { useState } from 'react';
import { ElementLayoutProps, LayoutElement, LayoutElementType, LayoutConfig } from './layoutTypes';
import { layoutElements } from './LayoutElements';
import { AVAILABLE_ELEMENT_TYPES } from '../index';
import EditModeControls from './EditModeControls';
import ElementEditor from './ElementEditor';
import EmptySlotWidget from '../../cards/widgets/EmptySlotWidget';



export default function ElementLayout({ 
  config, 
  className = '', 
  style = {}, 
  editMode = false,
  onConfigChange 
}: ElementLayoutProps) {
  
  const {
    elements,
    totalSlots,
    gap = 4,
    padding = 0,
    responsive = true,
    minElementWidth = 200,
    maxElementWidth = 400,
  } = config;

  // Determine the number of slots to display
  const numberOfSlots = totalSlots || Math.max(elements.length, 1);

  // Validate number of slots (1-10)
  if (numberOfSlots < 1 || numberOfSlots > 10) {
    console.warn(`ElementLayout: Number of slots (${numberOfSlots}) should be between 1 and 10`);
  }

  // Calculate flex basis to ensure equal distribution across full width
  const getFlexBasis = () => {
    return `${100 / numberOfSlots}%`;
  };

  // Generate responsive classes
  const getResponsiveClasses = () => {
    if (!responsive) return 'flex-row flex-nowrap';
    
    // For responsive layouts, we want cards to:
    // - Stack vertically on mobile (default)
    // - Display horizontally on larger screens based on number of cards
    // - Wrap if needed to prevent cards from being too narrow
    
    const baseClasses = ['flex-col'];
    
    // Determine breakpoint based on number of slots
    // More slots = need larger screen before going horizontal
    if (numberOfSlots <= 2) {
      // 1-2 cards: go horizontal at small breakpoint
      baseClasses.push('sm:flex-row');
    } else if (numberOfSlots <= 3) {
      // 3 cards: go horizontal at medium breakpoint
      baseClasses.push('md:flex-row');
    } else if (numberOfSlots <= 4) {
      // 4 cards: go horizontal at large breakpoint
      baseClasses.push('lg:flex-row');
    } else {
      // 5+ cards: go horizontal at extra large breakpoint
      baseClasses.push('xl:flex-row');
    }
    
    // Allow wrapping when horizontal to prevent cards from being too narrow
    baseClasses.push('flex-wrap');
    
    return baseClasses.join(' ');
  };

  // Edit mode handlers
  const handleAddElement = (type: LayoutElementType, slotIndex?: number) => {
    if (!onConfigChange || elements.length >= numberOfSlots) return;
    
    const newElement: LayoutElement = {
      id: `element-${Date.now()}`,
      type,
      props: {},
    };

    let newElements;
    if (slotIndex !== undefined) {
      // Insert at specific slot index
      newElements = [...elements];
      newElements.splice(slotIndex, 0, newElement);
    } else {
      // Add to the end
      newElements = [...elements, newElement];
    }

    const newConfig = {
      ...config,
      elements: newElements,
    };

    onConfigChange(newConfig);
  };

  const handleRemoveElement = (elementId: string) => {
    if (!onConfigChange) return;
    
    const newConfig = {
      ...config,
      elements: elements.filter(el => el.id !== elementId),
    };

    onConfigChange(newConfig);
  };

  const handleElementTypeChange = (elementId: string, newType: LayoutElementType) => {
    if (!onConfigChange) return;
    
    const newConfig = {
      ...config,
      elements: elements.map(el => 
        el.id === elementId ? { ...el, type: newType } : el
      ),
    };

    onConfigChange(newConfig);
  };

  const handleElementPropsChange = (elementId: string, newProps: Record<string, unknown>) => {
    if (!onConfigChange) return;
    
    const newConfig = {
      ...config,
      elements: elements.map(el => 
        el.id === elementId ? { ...el, props: newProps } : el
      ),
    };

    onConfigChange(newConfig);
  };

  const renderElement = (element: LayoutElement) => {
    const Component = layoutElements[element.type];
    
    if (!Component) {
      console.warn(`ElementLayout: Unknown element type "${element.type}"`);
      return (
        <div 
          key={element.id}
          className="flex-1 relative w-full self-stretch min-h-0"
          style={{ 
            flexBasis: responsive ? `${minElementWidth}px` : getFlexBasis(),
            minWidth: responsive ? `${minElementWidth}px` : '0',
          }}
        >
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 flex items-center justify-center h-full">
            <span className="text-red-600 text-sm">Unknown: {element.type}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        key={element.id}
        className="flex-1 relative w-full self-stretch min-h-0 flex flex-col"
        style={{ 
          flexBasis: responsive ? `${minElementWidth}px` : getFlexBasis(),
          minWidth: responsive ? `${minElementWidth}px` : '0',
        }}
      >
        {editMode ? (
          <ElementEditor
            element={element}
            onTypeChange={handleElementTypeChange}
            onRemove={handleRemoveElement}
            onPropsChange={handleElementPropsChange}
            availableTypes={[...AVAILABLE_ELEMENT_TYPES] as LayoutElementType[]}
          />
        ) : null}
        <Component {...element.props}>
          {element.children}
        </Component>
      </div>
    );
  };

  const renderSlot = (slotIndex: number) => {
    // For now, we'll use a simple approach where elements fill slots in order
    // In a more advanced implementation, you might want to add a slotIndex property to LayoutElement
    const element = elements[slotIndex];
    if (element) {
      return renderElement(element);
    } else if (editMode) {
      // Render empty slot widget
      return (
        <div
          key={`empty-slot-${slotIndex}`}
          className="flex-1 w-full self-stretch min-h-0"
          style={{ 
            flexBasis: responsive ? `${minElementWidth}px` : getFlexBasis(),
            minWidth: responsive ? `${minElementWidth}px` : '0',
          }}
        >
          <EmptySlotWidget
            onAddElement={(type) => handleAddElement(type, slotIndex)}
            availableTypes={[...AVAILABLE_ELEMENT_TYPES] as LayoutElementType[]}
            slotIndex={slotIndex}
          />
        </div>
      );
    } else {
      // In non-edit mode, render empty space
      return (
        <div
          key={`empty-slot-${slotIndex}`}
          className="flex-1 w-full self-stretch min-h-0"
          style={{ 
            flexBasis: responsive ? `${minElementWidth}px` : getFlexBasis(),
            minWidth: responsive ? `${minElementWidth}px` : '0',
          }}
        />
      );
    }
  };

  return (
    <div className={`${className} w-full max-w-full h-full min-h-0 overflow-visible flex-1 flex flex-col`} style={style}>
      <div
        className={`flex w-full max-w-full h-full min-h-0 overflow-visible flex-1 items-stretch ${getResponsiveClasses()}`}
        style={{
          padding: `${padding}px`,
          paddingBottom: `${padding + 4}px`, // Extra padding for card shadow
          paddingRight: `${padding + 4}px`, // Extra padding for card shadow
          gap: `${gap}px`,
        }}
      >
        {Array.from({ length: numberOfSlots }, (_, index) => renderSlot(index))}
      </div>
    </div>
  );
} 